/*
   Resolves a Viedoc language/culture code (as stored in the study settings'
   DateLanguage, or used as the app language) to a date-fns Locale object.
   Used when formatting dates with study patterns so that name tokens
   (MMMM, EEEE, ...) are rendered in the configured language.

   Follows the same approach as getDateTimePickerLocale for flatpickr:
   dynamic import of the locale file, with a map for the codes where the
   Viedoc name differs from the date-fns name.
*/

// Map Viedoc language codes to date-fns codes where they don't match
const localeMap = Object.freeze({
    "sr-Cyrl": "sr",
    "zh-Hans": "zh-CN",
    "zh-Hant": "zh-TW",
});

async function importDateFnsLocale(code) {
    // date-fns exports each locale under its code without dashes,
    // e.g. locale/zh-CN.js exports zhCN
    const exportName = code.replace(/-/g, "");

    try {
        const module = await import(
            `~/../node_modules/date-fns/locale/${code}.js`
        );

        return module[exportName];
    } catch {
        return undefined;
    }
}

/**
 * @param {string} language Viedoc language/culture code, e.g. "de", "zh-Hans"
 * @returns {Promise<import("date-fns").Locale | undefined>} the date-fns
 *   locale, or undefined when none is available (date-fns then uses English)
 */
export async function getDateFnsLocale(language) {
    if (!language) {
        return undefined;
    }

    const code = Object.hasOwn(localeMap, language)
        ? localeMap[language]
        : language;

    const locale = await importDateFnsLocale(code);
    if (locale) {
        return locale;
    }

    // Fall back to the base language for region codes date-fns doesn't have
    const baseLanguage = code.split("-")[0];
    if (baseLanguage !== code) {
        return importDateFnsLocale(baseLanguage);
    }

    return undefined;
}
